/**
 * BevisDialog - Merk dokument som bevis med juridisk metadata
 * Post-quantum kryptert bevissikring med automatisk Chain of Custody logging
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './BevisDialog.css';

export default function BevisDialog({
  sakId,
  documentId,
  documentNavn,
  onClose,
  onSuccess,
  eksisterendeBevis = null
}) {
  const [loading, setLoading] = useState(false);
  const [kategorier, setKategorier] = useState([]);
  const [lasterKategorier, setLasterKategorier] = useState(true);

  const [bevisData, setBevisData] = useState({
    bevis_for: '',
    bevis_type: 'dokument',
    bevis_type_detaljer: '',
    bevisverdi: 'normal',
    notater: '',
    konklusjon: '',
    juridisk_vurdering: '',
    kildeinfo: {
      opprinnelse: '',
      forfatter: '',
      dato: ''
    },
    tags: [],
    kategori_ids: []
  });

  const [nyTag, setNyTag] = useState('');
  const [aiForslag, setAiForslag] = useState(null);
  const [lasterAiForslag, setLasterAiForslag] = useState(false);

  // For opplasting av fysisk bevis
  const [fysiskBevisType, setFysiskBevisType] = useState('fil'); // 'fil', 'url', 'fysisk'
  const [valgtFil, setValgtFil] = useState(null);
  const [fysiskUrl, setFysiskUrl] = useState('');
  const [fysiskBeskrivelse, setFysiskBeskrivelse] = useState('');
  const [lasterOppFil, setLasterOppFil] = useState(false);

  // Bevistyper med beskrivelser
  const bevisTyper = [
    { value: 'materielt', label: 'Materielt bevis', emoji: '🔍', beskrivelse: 'Fysiske bevis, prøver, gjenstander' },
    { value: 'formelt', label: 'Formelt dokument', emoji: '📜', beskrivelse: 'Kontrakter, stevninger, dommer' },
    { value: 'teknisk', label: 'Teknisk rapport', emoji: '🔧', beskrivelse: 'Takstrapporter, ingeniørrapporter' },
    { value: 'vitne', label: 'Vitneforklaring', emoji: '👤', beskrivelse: 'Vitneerklæringer, vitneavhør' },
    { value: 'dokument', label: 'Generelt dokument', emoji: '📄', beskrivelse: 'Brev, notater, protokoller' },
    { value: 'elektronisk', label: 'Elektronisk bevis', emoji: '💻', beskrivelse: 'E-post, SMS, chat-logger' },
    { value: 'annet', label: 'Annet', emoji: '📋', beskrivelse: 'Annet bevismateriale' }
  ];

  const bevisverdier = [
    { value: 'kritisk', label: 'Kritisk', emoji: '🔴', beskrivelse: 'Avgjørende for saken' },
    { value: 'høy', label: 'Høy', emoji: '🟠', beskrivelse: 'Svært viktig' },
    { value: 'normal', label: 'Normal', emoji: '🟡', beskrivelse: 'Viktig' },
    { value: 'støttende', label: 'Støttende', emoji: '🟢', beskrivelse: 'Supplerende/støttende' }
  ];

  useEffect(() => {
    lastKategorier();

    // Hvis vi redigerer eksisterende bevis, fyll inn data
    if (eksisterendeBevis) {
      setBevisData({
        bevis_for: eksisterendeBevis.bevis_for || '',
        bevis_type: eksisterendeBevis.bevis_type || 'dokument',
        bevis_type_detaljer: eksisterendeBevis.bevis_type_detaljer || '',
        bevisverdi: eksisterendeBevis.bevisverdi || 'normal',
        notater: eksisterendeBevis.notater || '',
        konklusjon: eksisterendeBevis.konklusjon || '',
        juridisk_vurdering: eksisterendeBevis.juridisk_vurdering || '',
        kildeinfo: eksisterendeBevis.kildeinfo || { opprinnelse: '', forfatter: '', dato: '' },
        tags: eksisterendeBevis.tags || [],
        kategori_ids: eksisterendeBevis.kategorier?.map(k => k.id) || []
      });
    }
  }, [eksisterendeBevis]);

  const lastKategorier = async () => {
    try {
      setLasterKategorier(true);
      const response = await crmApi.request(`/bevis/kategorier?sak_id=${sakId}`);
      const data = await response.json();
      setKategorier(data.kategorier || []);
    } catch (error) {
      console.error('Feil ved lasting av kategorier:', error);
    } finally {
      setLasterKategorier(false);
    }
  };

  const hentAiForslag = async () => {
    if (!documentNavn) return;

    try {
      setLasterAiForslag(true);
      setAiForslag(null);

      // TODO: Implementer AI-endpoint for å foreslå bevistype, verdi og tags basert på dokumentnavn
      // For nå: enkel mock
      setTimeout(() => {
        setAiForslag({
          bevis_type: 'teknisk',
          bevisverdi: 'høy',
          tags: ['takst', 'mangel', 'eiendom'],
          forklaring: 'Basert på filnavnet tolker jeg dette som en teknisk rapport med høy bevisverdi.'
        });
        setLasterAiForslag(false);
      }, 1000);
    } catch (error) {
      console.error('Feil ved henting av AI-forslag:', error);
      setLasterAiForslag(false);
    }
  };

  const anvendAiForslag = () => {
    if (!aiForslag) return;

    setBevisData({
      ...bevisData,
      bevis_type: aiForslag.bevis_type,
      bevisverdi: aiForslag.bevisverdi,
      tags: [...new Set([...bevisData.tags, ...aiForslag.tags])]
    });
  };

  const leggTilTag = () => {
    if (!nyTag.trim()) return;
    if (bevisData.tags.includes(nyTag.trim())) {
      alert('Tag finnes allerede');
      return;
    }

    setBevisData({
      ...bevisData,
      tags: [...bevisData.tags, nyTag.trim()]
    });
    setNyTag('');
  };

  const fjernTag = (tag) => {
    setBevisData({
      ...bevisData,
      tags: bevisData.tags.filter(t => t !== tag)
    });
  };

  const toggleKategori = (kategoriId) => {
    if (bevisData.kategori_ids.includes(kategoriId)) {
      setBevisData({
        ...bevisData,
        kategori_ids: bevisData.kategori_ids.filter(id => id !== kategoriId)
      });
    } else {
      setBevisData({
        ...bevisData,
        kategori_ids: [...bevisData.kategori_ids, kategoriId]
      });
    }
  };

  const lagreBevis = async (e) => {
    e.preventDefault();

    if (!bevisData.bevis_for.trim()) {
      alert('Du må angi hva dokumentet skal bevise');
      return;
    }

    // Valider fysisk bevis-input hvis ingen documentId
    if (!documentId && !eksisterendeBevis) {
      if (fysiskBevisType === 'fil' && !valgtFil) {
        alert('Du må velge en fil å laste opp');
        return;
      }
      if (fysiskBevisType === 'url' && !fysiskUrl.trim()) {
        alert('Du må angi URL til beviset');
        return;
      }
      if (fysiskBevisType === 'fysisk' && !fysiskBeskrivelse.trim()) {
        alert('Du må beskrive det fysiske beviset');
        return;
      }
    }

    try {
      setLoading(true);
      let opprettetDocumentId = documentId;

      // Hvis vi skal laste opp fil først
      if (!documentId && !eksisterendeBevis && fysiskBevisType === 'fil' && valgtFil) {
        setLasterOppFil(true);
        try {
          const uploadedFile = await crmApi.uploadFil(sakId, valgtFil, 'bevis');
          opprettetDocumentId = uploadedFile.id;
          setLasterOppFil(false);
        } catch (error) {
          setLasterOppFil(false);
          throw new Error('Filopplasting feilet: ' + error.message);
        }
      }

      const payload = {
        sak_id: sakId,
        document_id: opprettetDocumentId,
        ...bevisData,
        // Legg til metadata for fysiske bevis
        ...(fysiskBevisType === 'url' && { external_url: fysiskUrl }),
        ...(fysiskBevisType === 'fysisk' && {
          fysisk_beskrivelse: fysiskBeskrivelse,
          oppbevaringssted: bevisData.kildeinfo.opprinnelse
        })
      };

      const url = eksisterendeBevis
        ? `/bevis/${eksisterendeBevis.id}`
        : '/bevis';

      const method = eksisterendeBevis ? 'PUT' : 'POST';

      const response = await crmApi.request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        alert(eksisterendeBevis
          ? '✓ Bevis oppdatert og beviskjede logget'
          : '✓ Bevis opprettet og sikret med beviskjede'
        );
        onSuccess?.(data);
        onClose();
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke lagre bevis'}`);
      }
    } catch (error) {
      console.error('Feil ved lagring av bevis:', error);
      alert('Kunne ikke lagre bevis: ' + error.message);
    } finally {
      setLoading(false);
      setLasterOppFil(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bevis-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="bevis-dialog-header">
          <h2>
            {eksisterendeBevis ? '✏️ Rediger bevis' : '🔒 Merk som bevis'}
          </h2>
          <p className="dokument-navn">📄 {documentNavn}</p>
          {!eksisterendeBevis && (
            <div className="sikkerhet-badge">
              🔐 Post-Quantum kryptert • Automatisk beviskjede
            </div>
          )}
        </div>

        <form onSubmit={lagreBevis}>
          {/* SECTION 0: Velg bevistype (kun hvis nytt bevis uten documentId) */}
          {!documentId && !eksisterendeBevis && (
            <div className="bevis-section">
              <h3>0. Type bevis</h3>
              <div className="bevistype-velger">
                <label className={`bevistype-option ${fysiskBevisType === 'fil' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="fysiskBevisType"
                    value="fil"
                    checked={fysiskBevisType === 'fil'}
                    onChange={(e) => setFysiskBevisType(e.target.value)}
                  />
                  <span className="option-icon">📎</span>
                  <span className="option-label">Last opp fil</span>
                  <span className="option-desc">Last opp et digitalt dokument</span>
                </label>

                <label className={`bevistype-option ${fysiskBevisType === 'url' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="fysiskBevisType"
                    value="url"
                    checked={fysiskBevisType === 'url'}
                    onChange={(e) => setFysiskBevisType(e.target.value)}
                  />
                  <span className="option-icon">🔗</span>
                  <span className="option-label">Link/URL</span>
                  <span className="option-desc">Pek til et eksternt dokument</span>
                </label>

                <label className={`bevistype-option ${fysiskBevisType === 'fysisk' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="fysiskBevisType"
                    value="fysisk"
                    checked={fysiskBevisType === 'fysisk'}
                    onChange={(e) => setFysiskBevisType(e.target.value)}
                  />
                  <span className="option-icon">📦</span>
                  <span className="option-label">Fysisk bevis</span>
                  <span className="option-desc">Beskrivelse av fysisk gjenstand</span>
                </label>
              </div>

              {/* Filopplasting */}
              {fysiskBevisType === 'fil' && (
                <div className="form-group">
                  <label>Velg fil *</label>
                  <input
                    type="file"
                    onChange={(e) => setValgtFil(e.target.files[0])}
                    accept="*/*"
                  />
                  {valgtFil && (
                    <div className="fil-valgt">
                      ✓ Valgt: {valgtFil.name} ({(valgtFil.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>
              )}

              {/* URL-input */}
              {fysiskBevisType === 'url' && (
                <div className="form-group">
                  <label>URL/Link *</label>
                  <input
                    type="url"
                    value={fysiskUrl}
                    onChange={(e) => setFysiskUrl(e.target.value)}
                    placeholder="https://..."
                    required
                  />
                  <span className="hint">Link til eksternt dokument eller nettside</span>
                </div>
              )}

              {/* Fysisk bevis-beskrivelse */}
              {fysiskBevisType === 'fysisk' && (
                <div className="form-group">
                  <label>Beskrivelse av fysisk bevis *</label>
                  <textarea
                    value={fysiskBeskrivelse}
                    onChange={(e) => setFysiskBeskrivelse(e.target.value)}
                    placeholder="F.eks. 'Metallprøve fra taket, oppbevart i forseglet pose nr. 12'"
                    rows={3}
                    required
                  />
                  <span className="hint">Beskriv det fysiske beviset og hvor det oppbevares</span>
                </div>
              )}
            </div>
          )}

          {/* SECTION 1: Hva skal bevises */}
          <div className="bevis-section">
            <h3>1. Hva skal dokumentet bevise?</h3>
            <div className="form-group">
              <label>Bevis for *</label>
              <textarea
                value={bevisData.bevis_for}
                onChange={(e) => setBevisData({...bevisData, bevis_for: e.target.value})}
                placeholder="F.eks. 'Dokumenterer mangel ved tak - vannlekkasje på overtakelsestidspunkt'"
                rows={2}
                required
              />
              <span className="hint">Beskriv konkret hva dokumentet beviser</span>
            </div>
          </div>

          {/* SECTION 2: Bevistype og verdi */}
          <div className="bevis-section">
            <h3>2. Klassifisering</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Bevistype</label>
                <select
                  value={bevisData.bevis_type}
                  onChange={(e) => setBevisData({...bevisData, bevis_type: e.target.value})}
                  className="bevis-type-select"
                >
                  {bevisTyper.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.emoji} {type.label}
                    </option>
                  ))}
                </select>
                <span className="hint">
                  {bevisTyper.find(t => t.value === bevisData.bevis_type)?.beskrivelse}
                </span>
              </div>

              <div className="form-group">
                <label>Bevisverdi</label>
                <select
                  value={bevisData.bevisverdi}
                  onChange={(e) => setBevisData({...bevisData, bevisverdi: e.target.value})}
                  className="bevisverdi-select"
                >
                  {bevisverdier.map((verdi) => (
                    <option key={verdi.value} value={verdi.value}>
                      {verdi.emoji} {verdi.label}
                    </option>
                  ))}
                </select>
                <span className="hint">
                  {bevisverdier.find(v => v.value === bevisData.bevisverdi)?.beskrivelse}
                </span>
              </div>
            </div>

            {/* AI-forslag */}
            <div className="ai-forslag-container">
              <button
                type="button"
                className="btn-ai"
                onClick={hentAiForslag}
                disabled={lasterAiForslag}
              >
                {lasterAiForslag ? '⏳ Analyserer...' : '🤖 AI-forslag'}
              </button>

              {aiForslag && (
                <div className="ai-forslag-box">
                  <div className="ai-forslag-header">
                    <span>💡 AI-forslag</span>
                    <button
                      type="button"
                      className="btn-apply-ai"
                      onClick={anvendAiForslag}
                    >
                      Bruk forslag
                    </button>
                  </div>
                  <p>{aiForslag.forklaring}</p>
                  <div className="ai-forslag-verdier">
                    <span>Type: <strong>{bevisTyper.find(t => t.value === aiForslag.bevis_type)?.label}</strong></span>
                    <span>Verdi: <strong>{bevisverdier.find(v => v.value === aiForslag.bevisverdi)?.label}</strong></span>
                    <span>Tags: {aiForslag.tags.map(tag => `#${tag}`).join(', ')}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Detaljert beskrivelse</label>
              <textarea
                value={bevisData.bevis_type_detaljer}
                onChange={(e) => setBevisData({...bevisData, bevis_type_detaljer: e.target.value})}
                placeholder="F.eks. 'Takstrapport fra Multiconsult AS, side 12-15, kapittel om takskader'"
                rows={2}
              />
            </div>
          </div>

          {/* SECTION 3: Juridisk vurdering */}
          <div className="bevis-section">
            <h3>3. Juridisk vurdering</h3>

            <div className="form-group">
              <label>Konklusjon</label>
              <textarea
                value={bevisData.konklusjon}
                onChange={(e) => setBevisData({...bevisData, konklusjon: e.target.value})}
                placeholder="F.eks. 'Beviser at selger kjente til mangelen før overtakelse'"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Juridisk vurdering</label>
              <textarea
                value={bevisData.juridisk_vurdering}
                onChange={(e) => setBevisData({...bevisData, juridisk_vurdering: e.target.value})}
                placeholder="F.eks. 'Styrker krav om prisavslag jf. avhendingsloven § 4-12'"
                rows={2}
              />
              <span className="hint">Bevisets betydning for saken og relevante lovparagrafer</span>
            </div>
          </div>

          {/* SECTION 4: Kildeinfo */}
          <div className="bevis-section">
            <h3>4. Kildeinformasjon</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Opprinnelse</label>
                <input
                  type="text"
                  value={bevisData.kildeinfo.opprinnelse}
                  onChange={(e) => setBevisData({
                    ...bevisData,
                    kildeinfo: {...bevisData.kildeinfo, opprinnelse: e.target.value}
                  })}
                  placeholder="F.eks. 'Mottatt fra takstmann via e-post 2024-01-15'"
                />
              </div>

              <div className="form-group">
                <label>Forfatter/Opphav</label>
                <input
                  type="text"
                  value={bevisData.kildeinfo.forfatter}
                  onChange={(e) => setBevisData({
                    ...bevisData,
                    kildeinfo: {...bevisData.kildeinfo, forfatter: e.target.value}
                  })}
                  placeholder="F.eks. 'Takstingeniør Ola Nordmann'"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Dato</label>
              <input
                type="date"
                value={bevisData.kildeinfo.dato}
                onChange={(e) => setBevisData({
                  ...bevisData,
                  kildeinfo: {...bevisData.kildeinfo, dato: e.target.value}
                })}
              />
            </div>
          </div>

          {/* SECTION 5: Notater */}
          <div className="bevis-section">
            <h3>5. Interne notater</h3>
            <div className="form-group">
              <label>Notater</label>
              <textarea
                value={bevisData.notater}
                onChange={(e) => setBevisData({...bevisData, notater: e.target.value})}
                placeholder="Interne notater om beviset..."
                rows={3}
              />
              <span className="hint">🔒 Kryptert - kun synlig for advokater på saken</span>
            </div>
          </div>

          {/* SECTION 6: Tags og kategorier */}
          <div className="bevis-section">
            <h3>6. Organisering</h3>

            <div className="form-group">
              <label>Tags (søkeord)</label>
              <div className="tags-input-container">
                <input
                  type="text"
                  value={nyTag}
                  onChange={(e) => setNyTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), leggTilTag())}
                  placeholder="Legg til tag..."
                />
                <button type="button" onClick={leggTilTag} className="btn-add-tag">
                  + Legg til
                </button>
              </div>

              <div className="tags-list">
                {bevisData.tags.map((tag) => (
                  <span key={tag} className="tag">
                    #{tag}
                    <button type="button" onClick={() => fjernTag(tag)}>×</button>
                  </span>
                ))}
              </div>
              <span className="hint">Trykk Enter eller klikk 'Legg til' for å legge til tag</span>
            </div>

            <div className="form-group">
              <label>Kategorier</label>
              {lasterKategorier ? (
                <p>Laster kategorier...</p>
              ) : kategorier.length === 0 ? (
                <p className="hint">Ingen kategorier opprettet ennå</p>
              ) : (
                <div className="kategorier-grid">
                  {kategorier.map((kategori) => (
                    <label
                      key={kategori.id}
                      className={`kategori-checkbox ${bevisData.kategori_ids.includes(kategori.id) ? 'checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={bevisData.kategori_ids.includes(kategori.id)}
                        onChange={() => toggleKategori(kategori.id)}
                      />
                      <span
                        className="kategori-farge"
                        style={{ backgroundColor: kategori.farge }}
                      />
                      <span className="kategori-navn">{kategori.navn}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              Avbryt
            </button>
            <button type="submit" className="btn-primary" disabled={loading || lasterOppFil}>
              {lasterOppFil ? '📤 Laster opp fil...' : (loading ? '⏳ Lagrer...' : (eksisterendeBevis ? '💾 Oppdater bevis' : '🔒 Sikre som bevis'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
